import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { useOrg } from '../context/OrgContext';
import { getCurrentUser, logout } from '../services/auth';
import { studentApi } from '../services/api';
import { Button } from './ui';
import ErrorBoundary from './ErrorBoundary';
import InstituteMark from './InstituteMark';
import JoinCodeCard from './JoinCodeCard';
import { instituteBanner } from '../utils/institute';
import logo from '../assets/icon.png';

// Grouped in the order an institute actually works: set things up, teach,
// then look at how it went. Settings sits apart at the bottom.
const NAV_GROUPS = [
  {
    label: 'Set up',
    items: [
      ['/exam-library', 'Exam library', <LocalLibraryOutlinedIcon fontSize="small" />],
      ['/batches', 'Batches', <GroupsOutlinedIcon fontSize="small" />],
      ['/students', 'Students', <PersonOutlineOutlinedIcon fontSize="small" />, 'pending'],
    ],
  },
  {
    label: 'Teach',
    items: [
      ['/manage-quiz', 'Tests', <RocketLaunchOutlinedIcon fontSize="small" />],
      ['/question-banks', 'Question banks', <QuizOutlinedIcon fontSize="small" />],
      ['/content', 'Notes & lessons', <MenuBookOutlinedIcon fontSize="small" />],
    ],
  },
  {
    label: 'Track',
    items: [
      ['/', 'Dashboard', <InsightsOutlinedIcon fontSize="small" />],
    ],
  },
];

const TITLES = {
  '/': 'Dashboard',
  '/welcome': 'Welcome',
  '/exam-library': 'Exam library',
  '/question-banks': 'Question banks',
  '/manage-quiz': 'Tests',
  '/screener': 'Compose a test',
  '/content': 'Notes & lessons',
  '/students': 'Students',
  '/batches': 'Batches',
  '/settings': 'Settings'
};

// Match detail routes (e.g. /exam-library/:examId) to their module title.
function pageTitle(pathname) {
  if (TITLES[pathname]) return TITLES[pathname];
  const match = Object.keys(TITLES).find((path) => path !== '/' && pathname.startsWith(`${path}/`));
  return match ? TITLES[match] : 'Institute console';
}

export default function Layout() {
  const org = useOrg();
  const location = useLocation();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const initials = (user?.displayName || user?.email || '?').split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
  const [pendingCount, setPendingCount] = useState(0);

  // A small badge on Students when someone is waiting for approval. Refreshed
  // on navigation so it stays roughly current without polling.
  useEffect(() => {
    let cancelled = false;
    studentApi.list(org.id)
      .then((res) => {
        if (cancelled) return;
        const pending = (res.data || []).filter((s) => s.status !== 'active' && !s.invitedEmail).length;
        setPendingCount(pending);
      })
      .catch(() => { /* badge is best-effort */ });
    return () => { cancelled = true; };
  }, [org.id, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const tagline = org?.metadata?.tagline;
  // Shown on every page while the institute awaits activation (or is
  // suspended), so the owner is never surprised by a refused invite.
  const banner = instituteBanner(org);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand-institute">
          <InstituteMark org={org} size={44} />
          <div>
            <span className="brand-name" title={org?.name}>{org?.name}</span>
            <span className="brand-tagline">{tagline || 'Institute console'}</span>
          </div>
        </div>
        <JoinCodeCard code={org?.code} compact />
        <nav>
          {NAV_GROUPS.map((group) => (
            <div className="nav-group" key={group.label}>
              <span className="nav-group-label">{group.label}</span>
              {group.items.map(([to, label, icon, badge]) => (
                <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => (isActive ? 'nav active' : 'nav')}>
                  {icon}{label}
                  {badge === 'pending' && pendingCount > 0 && (
                    <span className="nav-badge" title={`${pendingCount} waiting for approval`}>{pendingCount}</span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="nav-group">
            <NavLink to="/settings" className={({ isActive }) => (isActive ? 'nav active' : 'nav')}>
              <SettingsOutlinedIcon fontSize="small" />Settings
            </NavLink>
          </div>
        </nav>
        <div className="brand-powered">
          <img src={logo} alt="" />Powered by Prepgen.ai
        </div>
        <div className="sidebar-footer">
          <span className="avatar">{initials}</span>
          <span>{user?.displayName || user?.email}<small>Institute owner</small></span>
        </div>
      </aside>
      <main>
        <header>
          <div>
            <p className="eyebrow">{org?.name?.toUpperCase()}</p>
            <h1>{pageTitle(location.pathname)}</h1>
          </div>
          <div className="header-actions">
            <span className="seat">{org?.memberCount ?? 0} members</span>
            <Button variant="text" onClick={handleLogout}>Log out</Button>
          </div>
        </header>
        <div className="page">
          {banner && <div className="notice-row" role="status"><span>{banner}</span></div>}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
